/**
 * 🏢 Enterprise Context Provider (Vague 4 P0)
 * ============================================
 *
 * Source de vérité côté client pour le contexte collaborateur entreprise.
 * Consomme uniquement `GET /api/me/enterprise-context` — ZÉRO règle de
 * filtrage implémentée dans le frontend.
 *
 * Expose :
 *   - isCollaborator: bool
 *   - orders: [{order_id, company_name, sessions_remaining, valid_until, ...}]
 *   - totalSessionsRemaining: number
 *   - eligibleCoachIds: Set<string>
 *   - nextExpiryAt: string | null
 *   - refresh(): refetch manuel (après un booking par ex.)
 *
 * Utilisation :
 *   const { isCollaborator, eligibleCoachIds } = useEnterpriseContext();
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

const EnterpriseContext = createContext({
  isCollaborator: false,
  orders: [],
  totalSessionsRemaining: 0,
  eligibleCoachIds: new Set(),
  nextExpiryAt: null,
  loading: true,
  refresh: () => {},
});

export function EnterpriseContextProvider({ children }) {
  const { token } = useAuth();
  const [ctx, setCtx] = useState({
    isCollaborator: false, orders: [],
    totalSessionsRemaining: 0, eligibleCoachIds: new Set(),
    nextExpiryAt: null, loading: true,
  });

  const refresh = useCallback(async () => {
    if (!token) {
      setCtx({
        isCollaborator: false, orders: [],
        totalSessionsRemaining: 0, eligibleCoachIds: new Set(),
        nextExpiryAt: null, loading: false,
      });
      return;
    }
    try {
      const res = await fetch(`${API}/me/enterprise-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('ctx_fetch_failed');
      const d = await res.json();
      setCtx({
        isCollaborator: !!d.is_collaborator,
        orders: d.orders || [],
        totalSessionsRemaining: d.total_sessions_remaining || 0,
        eligibleCoachIds: new Set(d.eligible_coach_ids || []),
        nextExpiryAt: d.next_expiry_at || null,
        loading: false,
      });
    } catch {
      setCtx({
        isCollaborator: false, orders: [],
        totalSessionsRemaining: 0, eligibleCoachIds: new Set(),
        nextExpiryAt: null, loading: false,
      });
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ ...ctx, refresh }), [ctx, refresh]);

  return (
    <EnterpriseContext.Provider value={value}>
      {children}
    </EnterpriseContext.Provider>
  );
}

export function useEnterpriseContext() {
  return useContext(EnterpriseContext);
}
